import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight } from "@material-symbols-svg/react";
import moment from "moment";

import AddButtonOverlay from "src/components/common/AddButtonOverlay";
import DropdownV2 from "src/components/common/DropdownV2";
import InsightHistory from "./InsightHistory";
import PeriodNavigator, { PeriodSegment } from "./PeriodNavigator";
import BarChart, { BarChartDatum } from "../charts/BarChart";
import InsightsProgressCard from "./InsightsProgressCard";

import { useAppSelector } from "src/redux/hooks";
import insightsApis from "src/api/insights";

import type { InsightComponentHealthOption } from "./InsightComponentHealth";

type FilterType = "day" | "week" | "month";

const TAB_TO_FILTER: Record<string, FilterType> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
};

/** Keys in diet logger graph_data; dropdown value = API key */
const DIET_OPTIONS = [
  { label: "Calories", value: "cals" },
  { label: "Protein", value: "proteins" },
  { label: "Fats", value: "fats" },
  // { label: "Carbohydrates", value: "carbs" },
];

const DIET_KEY_UNITS: Record<string, string> = {
  cals: "kcal",
  proteins: "g",
  fats: "g",
  carbs: "g",
};

function toApiDate(m: moment.Moment): string {
  return m.format("DD/MM/YYYY");
}

function toApiMonth(m: moment.Moment): string {
  return m.format("MMM").toUpperCase();
}

/** API /gfc/loggers diet response graph_data item */
interface DietGraphItem {
  timestamp?: string;
  cals?: number;
  proteins?: number;
  fats?: number;
  carbs?: number;
  [key: string]: unknown;
}

interface InsightComponentDietProps {
  insightType: string;
  parameter: string;
  barColor?: string;
  icon?: React.ReactNode;
  supportProgramKey: string;
  options?: InsightComponentHealthOption[];
}

const InsightComponentDiet: React.FC<InsightComponentDietProps> = ({
  barColor = "#EA5803",
  icon,
  supportProgramKey,
  options = [],
}) => {
  const { patient } = useAppSelector((state) => state.auth);

  const [viewType, setViewType] = useState<"chart" | "history">("chart");
  const [activeTab, setActiveTab] = useState<PeriodSegment>("daily");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [periodOffset, setPeriodOffset] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string>("cals");
  const [graphData, setGraphData] = useState<DietGraphItem[]>([]);
  const [unitFromApi, setUnitFromApi] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const buildQueryParams = useCallback(() => {
    const filterType = TAB_TO_FILTER[activeTab] ?? "day";
    const base: Record<string, string | number> = {
      user_id: patient?.data?.patient_id ?? "",
      logger: "diet",
      filter_type: filterType,
    };
    if (filterType === "day") {
      const date = moment().subtract(periodOffset, "day");
      base.date = toApiDate(date);
    } else if (filterType === "week") {
      const start = moment().subtract(periodOffset, "week").startOf("week");
      const end = start.clone().endOf("week");
      base.start_date = toApiDate(start);
      base.end_date = toApiDate(end);
    } else {
      const monthM = moment().subtract(periodOffset, "month").startOf("month");
      base.month = toApiMonth(monthM);
      base.year = monthM.year();
    }
    return base;
  }, [activeTab, periodOffset, patient?.data?.patient_id]);

  const fetchLogs = useCallback(async () => {
    const userId = patient?.data?.patient_id;
    if (!userId) {
      setGraphData([]);
      setUnitFromApi(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const queryParams = buildQueryParams();
      const res: any = await insightsApis.getLogData(queryParams);
      const code = res?.code != null ? Number(res.code) : null;
      if (code === -1 || code === 0) {
        setGraphData([]);
        setUnitFromApi(null);
        return;
      }
      const list = res?.data?.graph_data ?? [];
      setGraphData(Array.isArray(list) ? list : []);
      setUnitFromApi(res?.data?.unit ?? null);
    } catch {
      setGraphData([]);
      setUnitFromApi(null);
    } finally {
      setLoading(false);
    }
  }, [patient?.data?.patient_id, buildQueryParams]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toTimestamp = (ts: string): number | null => {
    if (!ts) return null;
    const cleaned = String(ts).replace("TZ", "T");
    const m = moment(cleaned, [moment.ISO_8601, "YYYY-MM-DD"], true);
    return m.isValid() ? m.valueOf() : null;
  };

  const toYMD = (ts: string): string | null => {
    if (!ts) return null;
    const cleaned = String(ts).replace("TZ", "T");
    const m = moment(cleaned, [moment.ISO_8601, "YYYY-MM-DD"], true);
    return m.isValid() ? m.format("YYYY-MM-DD") : null;
  };

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (activeTab === "daily") {
      const d = moment().subtract(periodOffset, "day");
      return {
        rangeStart: d.clone().startOf("day").valueOf(),
        rangeEnd: d.clone().endOf("day").valueOf(),
      };
    }
    if (activeTab === "weekly") {
      const start = moment().subtract(periodOffset, "week").startOf("week");
      return {
        rangeStart: start.valueOf(),
        rangeEnd: start.clone().endOf("week").valueOf(),
      };
    }
    const start = moment().subtract(periodOffset, "month").startOf("month");
    return {
      rangeStart: start.valueOf(),
      rangeEnd: start.clone().endOf("month").valueOf(),
    };
  }, [activeTab, periodOffset]);

  const chartData = useMemo((): BarChartDatum[] => {
    const valueKey = selectedOption;
    const valueByDay = new Map<string, number>();
    graphData.forEach((item) => {
      const ymd = toYMD(item?.timestamp as string);
      if (!ymd) return;
      const t = toTimestamp(item?.timestamp as string);
      if (t == null || t < rangeStart || t > rangeEnd) return;
      const val = item[valueKey];
      const num = typeof val === "number" && Number.isFinite(val) ? val : 0;
      valueByDay.set(ymd, (valueByDay.get(ymd) ?? 0) + num);
    });

    if (activeTab === "daily") {
      const d = moment(rangeStart);
      const ymd = d.format("YYYY-MM-DD");
      const value = valueByDay.get(ymd) ?? 0;
      const label =
        periodOffset === 0
          ? "Today"
          : periodOffset === 1
          ? "Yesterday"
          : d.format("D MMM");
      return [{ label, value }];
    }

    if (activeTab === "weekly") {
      const start = moment(rangeStart);
      return Array.from({ length: 7 }, (_, i) => {
        const day = start.clone().add(i, "day");
        const ymd = day.format("YYYY-MM-DD");
        const value = valueByDay.get(ymd) ?? 0;
        return { label: day.format("D MMM"), value };
      });
    }

    if (activeTab === "monthly") {
      const start = moment(rangeStart);
      const daysInMonth = moment(rangeEnd).date();
      return Array.from({ length: daysInMonth }, (_, i) => {
        const day = start.clone().date(i + 1);
        const ymd = day.format("YYYY-MM-DD");
        const value = valueByDay.get(ymd) ?? 0;
        return { label: day.format("D"), value };
      });
    }

    return [];
  }, [
    graphData,
    selectedOption,
    rangeStart,
    rangeEnd,
    activeTab,
    periodOffset,
  ]);

  const unit = DIET_KEY_UNITS[selectedOption] ?? unitFromApi ?? "kcal";

  const noDataForPeriod =
    !loading && graphData.length > 0 && chartData.every((d) => d.value === 0);
  const noDataAtAll = !loading && graphData.length === 0;

  if (viewType === "history") {
    return (
      <InsightHistory
        onBackClick={() => setViewType("chart")}
        supportProgramKey={supportProgramKey}
        logger="food"
        icon={icon}
      />
    );
  }

  return (
    <div className="relative pb-12">
      <div className="px-4 space-y-4">
        <InsightsProgressCard loggerType="diet" />

        <DropdownV2
          options={DIET_OPTIONS}
          value={selectedOption}
          onChange={(value) => setSelectedOption(value as string)}
        />

        <div className="bg-white rounded-xl p-4 space-y-4">
          <PeriodNavigator
            activeTab={activeTab}
            periodOffset={periodOffset}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setPeriodOffset(0);
            }}
            onPrev={() => setPeriodOffset((p) => p + 1)}
            onNext={() => setPeriodOffset((p) => Math.max(0, p - 1))}
          />

          {loading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-[var(--app-primary)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : noDataForPeriod || noDataAtAll ? (
            <div className="h-40 flex items-center justify-center text-sm text-gray-500">
              No data for this period
            </div>
          ) : (
            <BarChart
              data={chartData}
              unit={unit}
              activeIndex={activeIndex}
              onBarClick={(index) => setActiveIndex(index)}
              barColor={barColor}
            />
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <button
          type="button"
          onClick={() => setViewType("history")}
          className="w-full bg-white rounded-xl px-4 py-3 flex items-center justify-between shadow-sm border border-gray-100"
        >
          <span>Show All Data</span>
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <AddButtonOverlay options={options} />
    </div>
  );
};

export default InsightComponentDiet;
